import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveKit } from "@/contexts/LiveKitContext";
import axios from "axios";
import { toast } from "sonner";
import EventChatWidget from "@/components/EventChatWidget";
import {
  Video, BarChart3, Users, Radio, MessageCircle, QrCode,
  Maximize2, Minimize2, X, Columns2, Columns3, Grid2x2,
  LayoutGrid, ArrowLeft, Settings, ExternalLink, Send,
  Share2, Sliders, Copy, Link as LinkIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Lazy imports for panels
const PANEL_REGISTRY = {
  conference: { label: "Conference", icon: Video, color: "#10B981" },
  stats: { label: "Statistiques", icon: BarChart3, color: "#0099CC" },
  participants: { label: "Participants", icon: Users, color: "#8B5CF6" },
  regie: { label: "Regie Live", icon: Radio, color: "#EF4444" },
  chat: { label: "Chat & Q/A", icon: MessageCircle, color: "#F59E0B" },
  scanner: { label: "Scanner QR", icon: QrCode, color: "#06B6D4" },
  sharing: { label: "Partage & Liens", icon: Share2, color: "#25D366" },
  production: { label: "Production", icon: Sliders, color: "#EC4899" },
};

const LAYOUTS = [
  { id: 1, label: "1 panneau", icon: Maximize2, grid: "grid-cols-1 grid-rows-1" },
  { id: 2, label: "2 panneaux", icon: Columns2, grid: "grid-cols-2 grid-rows-1" },
  { id: 3, label: "3 panneaux", icon: Columns3, grid: "grid-cols-3 grid-rows-1" },
  { id: 4, label: "4 panneaux", icon: Grid2x2, grid: "grid-cols-2 grid-rows-2" },
  { id: 6, label: "6 panneaux", icon: LayoutGrid, grid: "grid-cols-3 grid-rows-2" },
];

function PanelFrame({ panelId, panelType, onClose, onDetach, children }) {
  const reg = PANEL_REGISTRY[panelType] || {};
  const Icon = reg.icon || Video;

  return (
    <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl overflow-hidden flex flex-col" data-testid={`panel-${panelId}`}>
      <div className="h-8 bg-[#151B2B] flex items-center justify-between px-2 shrink-0 border-b border-[#1E293B]">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3 w-3" style={{ color: reg.color }} />
          <span className="text-xs font-medium text-white">{reg.label}</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onDetach(panelId, panelType)}
            className="p-0.5 text-[#64748b] hover:text-white rounded transition-colors" title="Detacher dans une nouvelle fenetre">
            <ExternalLink className="h-3 w-3" />
          </button>
          <button onClick={() => onClose(panelId)}
            className="p-0.5 text-[#64748b] hover:text-red-400 rounded transition-colors" title="Fermer">
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        {children}
      </div>
    </div>
  );
}

// ===== PANEL CONTENT COMPONENTS =====

function ConferencePanel({ eventId }) {
  const lk = useLiveKit();
  const navigate = useNavigate();

  return (
    <div className="h-full flex flex-col items-center justify-center p-4 text-center" data-testid="panel-conference-content">
      <div className={`w-3 h-3 rounded-full mb-3 ${lk.connectionState === "connected" ? "bg-[#10B981] animate-pulse" : "bg-[#64748b]"}`} />
      <p className="text-white text-sm font-semibold mb-1">
        {lk.connectionState === "connected" ? "Conference active" : "Aucune conference"}
      </p>
      <p className="text-[#64748b] text-xs mb-4">
        {lk.connectionState === "connected" ? `${lk.formatTime(lk.elapsed)} — ${lk.lkParticipants.length || "—"} participant(s)` : "Rejoignez le live depuis la fiche evenement"}
      </p>
      {lk.connectionState === "connected" ? (
        <button onClick={() => navigate(`/app/live-meeting/${lk.activeRoomId}`)}
          className="px-4 py-2 rounded-lg bg-[#10B981]/15 text-[#10B981] text-sm font-medium hover:bg-[#10B981]/25 transition-all"
          data-testid="panel-open-live">
          <Video className="h-4 w-4 inline mr-1.5" />Ouvrir en plein ecran
        </button>
      ) : (
        <button onClick={() => navigate(`/app/live-meeting/${eventId}`)}
          className="px-4 py-2 rounded-lg bg-[#0099CC]/15 text-[#0099CC] text-sm font-medium hover:bg-[#0099CC]/25 transition-all"
          data-testid="panel-join-live">
          <Video className="h-4 w-4 inline mr-1.5" />Rejoindre le Live
        </button>
      )}
    </div>
  );
}

function StatsPanel({ eventId, headers }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/event/events/${eventId}/stats/advanced?period=all`, { headers });
        setStats(res.data);
      } catch {}
    };
    fetch();
    const iv = setInterval(fetch, 15000);
    return () => clearInterval(iv);
  }, [eventId]);

  if (!stats) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0099CC]" /></div>;

  return (
    <div className="p-3 space-y-3" data-testid="panel-stats-content">
      <div className="grid grid-cols-3 gap-2">
        {[
          { v: stats.total, l: "Inscrits", c: "#0099CC" },
          { v: stats.checked_in, l: "Presents", c: "#10B981" },
          { v: stats.cancelled, l: "Annules", c: "#EF4444" },
        ].map(k => (
          <div key={k.l} className="bg-[#151B2B] rounded-lg p-2 text-center">
            <div className="text-xl font-bold" style={{ color: k.c }}>{k.v}</div>
            <div className="text-[10px] text-[#64748b]">{k.l}</div>
          </div>
        ))}
      </div>
      <div className="bg-[#151B2B] rounded-lg p-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b]">Taux de presence</span>
          <span className="font-bold text-[#10B981]">{stats.attendance_rate?.toFixed(1)}%</span>
        </div>
        <div className="h-2 rounded-full bg-[#1E293B] mt-2 overflow-hidden">
          <div className="h-full rounded-full bg-[#10B981] transition-all" style={{ width: `${stats.attendance_rate || 0}%` }} />
        </div>
      </div>
    </div>
  );
}

function ParticipantsPanel({ eventId, headers }) {
  const [participants, setParticipants] = useState([]);
  const [search, setSearch] = useState("");
  const [inviting, setInviting] = useState(null);
  const [invMsg, setInvMsg] = useState("");
  const [showInvModal, setShowInvModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    const load = () => {
      axios.get(`${API}/api/event/events/${eventId}/participants`, { headers })
        .then(r => setParticipants(r.data)).catch(() => {});
    };
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [eventId]);

  const ROLE_COLORS = { moderator: "#F59E0B", speaker: "#10B981", participant: "#64748b" };
  const ROLE_LABELS = { moderator: "Moderateur", speaker: "Intervenant", participant: "Participant" };

  const setRole = async (pid, role) => {
    try {
      await axios.patch(`${API}/api/event/events/${eventId}/participants/${pid}/conference-role`, { conference_role: role }, { headers });
      setParticipants(ps => ps.map(p => p.id === pid ? { ...p, conference_role: role } : p));
      toast.success(`Role: ${ROLE_LABELS[role]}`);
    } catch { toast.error("Erreur"); }
  };

  const sendInvitations = async () => {
    if (selectedIds.size === 0) return;
    setInviting("sending");
    try {
      const res = await axios.post(`${API}/api/event/events/${eventId}/send-invitations`, {
        participant_ids: Array.from(selectedIds),
        custom_message: invMsg,
      }, { headers });
      if (res.data.sent > 0) toast.success(`${res.data.sent} invitation(s) envoyee(s)`);
      if (res.data.failed > 0) toast.warning(`${res.data.failed} echec(s)`);
      setSelectedIds(new Set());
      setShowInvModal(false);
      setInvMsg("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally { setInviting(null); }
  };

  const toggleSelect = (pid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const filtered = participants.filter(p => {
    const q = search.toLowerCase();
    return !q || `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(q);
  });

  return (
    <div className="flex flex-col h-full" data-testid="panel-participants-content">
      <div className="p-2 border-b border-[#1E293B] space-y-1.5">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
          className="w-full bg-[#151B2B] border border-[#2A3441] rounded-lg px-3 py-1.5 text-sm text-white placeholder-[#4A5568]" />
        <div className="flex items-center gap-1.5">
          {selectedIds.size > 0 && (
            <button onClick={() => setShowInvModal(true)}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#10B981]/15 text-[#10B981] text-[10px] font-medium hover:bg-[#10B981]/25 transition-all"
              data-testid="ctrl-invite-btn">
              <Send className="h-2.5 w-2.5" /> Inviter ({selectedIds.size})
            </button>
          )}
          <span className="text-[10px] text-[#4A5568]">{filtered.length} participant(s)</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.map(p => (
          <div key={p.id} className={`flex items-center gap-2 px-2 py-1.5 hover:bg-[#151B2B] group transition-colors ${selectedIds.has(p.id) ? "bg-[#0099CC]/5" : ""}`} data-testid={`ctrl-participant-${p.id}`}>
            <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)}
              className="rounded border-[#2A3441] h-3 w-3 shrink-0" />
            <div className="w-7 h-7 rounded-full bg-[#0099CC]/20 flex items-center justify-center text-[10px] font-bold text-[#0099CC] shrink-0">
              {(p.first_name?.[0] || "")}{(p.last_name?.[0] || "")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-xs font-medium truncate">{p.first_name} {p.last_name}</div>
              <div className="text-[10px] text-[#64748b] truncate">{p.email}</div>
            </div>
            <select
              value={p.conference_role || "participant"}
              onChange={e => setRole(p.id, e.target.value)}
              className="bg-[#0B0F19] border border-[#2A3441] rounded px-1.5 py-0.5 text-[10px] font-medium transition-opacity"
              style={{ color: ROLE_COLORS[p.conference_role || "participant"] }}
              data-testid={`role-select-${p.id}`}
            >
              <option value="participant">Participant</option>
              <option value="speaker">Intervenant</option>
              <option value="moderator">Moderateur</option>
            </select>
            {p.conference_role && p.conference_role !== "participant" && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0"
                style={{ color: ROLE_COLORS[p.conference_role], background: ROLE_COLORS[p.conference_role] + "20" }}>
                {ROLE_LABELS[p.conference_role]}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Invitation modal */}
      {showInvModal && (
        <div className="absolute inset-0 z-10 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setShowInvModal(false)}>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4 max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="ctrl-inv-modal">
            <h4 className="text-white text-sm font-semibold mb-2">Envoyer des invitations</h4>
            <p className="text-[10px] text-[#64748b] mb-3">{selectedIds.size} participant(s) selectionne(s)</p>
            <textarea value={invMsg} onChange={e => setInvMsg(e.target.value)}
              placeholder="Message personnalise (optionnel)..."
              className="w-full bg-[#0B0F19] border border-[#2A3441] rounded-lg p-2 text-xs text-white placeholder-[#4A5568] resize-none h-16 mb-3" />
            <div className="flex gap-2">
              <button onClick={() => setShowInvModal(false)} className="flex-1 px-3 py-1.5 rounded-lg bg-[#1E293B] text-[#94A3B8] text-xs hover:bg-[#2A3441] transition-colors">Annuler</button>
              <button onClick={sendInvitations} disabled={inviting === "sending"}
                className="flex-1 px-3 py-1.5 rounded-lg bg-[#10B981] text-white text-xs font-semibold hover:bg-[#059669] transition-colors disabled:opacity-50"
                data-testid="ctrl-send-inv-btn">
                {inviting === "sending" ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RegiePanel({ eventId }) {
  const navigate = useNavigate();
  return (
    <div className="h-full flex flex-col items-center justify-center p-4 text-center" data-testid="panel-regie-content">
      <Radio className="h-8 w-8 text-[#EF4444] mb-3" />
      <p className="text-white text-sm font-semibold mb-1">Regie Live</p>
      <p className="text-[#64748b] text-xs mb-4">Gerez les sources, preview et programme de diffusion</p>
      <button onClick={() => navigate(`/app/evenements/${eventId}/live-control`)}
        className="px-4 py-2 rounded-lg bg-[#EF4444]/15 text-[#EF4444] text-sm font-medium hover:bg-[#EF4444]/25 transition-all"
        data-testid="panel-open-regie">
        <Radio className="h-4 w-4 inline mr-1.5" />Ouvrir la Regie
      </button>
    </div>
  );
}

function ChatPanel({ eventId, token }) {
  return (
    <div className="h-full" data-testid="panel-chat-content">
      <EventChatWidget eventId={eventId} token={token} compact={true} />
    </div>
  );
}

function ScannerPanel({ eventId }) {
  const navigate = useNavigate();
  return (
    <div className="h-full flex items-center justify-center p-4 text-center" data-testid="panel-scanner-content">
      <div>
        <QrCode className="h-8 w-8 text-[#06B6D4] mx-auto mb-3" />
        <p className="text-white text-sm font-semibold mb-1">Scanner QR Code</p>
        <p className="text-[#64748b] text-xs mb-4">Scannez les badges des participants</p>
        <button onClick={() => navigate(`/app/evenements/${eventId}/scanner`)}
          className="px-4 py-2 rounded-lg bg-[#06B6D4]/15 text-[#06B6D4] text-sm font-medium hover:bg-[#06B6D4]/25 transition-all">
          Ouvrir le Scanner
        </button>
      </div>
    </div>
  );
}

function SharingPanel({ eventId, event }) {
  const publicUrl = event ? `${window.location.origin}/e/${event.org_slug || "org"}/${event.slug || eventId}` : "";
  const joinUrl = event ? `${window.location.origin}/event-join/${eventId}` : "";

  const copyLink = (url, label) => {
    navigator.clipboard.writeText(url).then(() => toast.success(`${label} copie`)).catch(() => {});
  };

  const shareWhatsApp = (url, text) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(text + "\n" + url)}`, "_blank");
  };

  return (
    <div className="p-3 space-y-3" data-testid="panel-sharing-content">
      <div className="space-y-2">
        <div className="bg-[#151B2B] rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <LinkIcon className="h-3 w-3 text-[#0099CC]" />
            <span className="text-xs text-white font-medium">Lien public</span>
          </div>
          <div className="flex gap-1.5">
            <input readOnly value={publicUrl} className="flex-1 bg-[#0B0F19] border border-[#2A3441] rounded px-2 py-1 text-[10px] text-[#94A3B8] truncate" />
            <button onClick={() => copyLink(publicUrl, "Lien")} className="px-2 py-1 rounded bg-[#0099CC]/15 text-[#0099CC] hover:bg-[#0099CC]/25 transition-all">
              <Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className="bg-[#151B2B] rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Video className="h-3 w-3 text-[#10B981]" />
            <span className="text-xs text-white font-medium">Lien conference</span>
          </div>
          <div className="flex gap-1.5">
            <input readOnly value={joinUrl} className="flex-1 bg-[#0B0F19] border border-[#2A3441] rounded px-2 py-1 text-[10px] text-[#94A3B8] truncate" />
            <button onClick={() => copyLink(joinUrl, "Lien conference")} className="px-2 py-1 rounded bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 transition-all">
              <Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        <button onClick={() => shareWhatsApp(publicUrl, event?.title || "Evenement GLOTECH")}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] text-xs font-medium hover:bg-[#25D366]/20 transition-all"
          data-testid="share-whatsapp">
          <Share2 className="h-3.5 w-3.5" /> Partager via WhatsApp
        </button>
        <button onClick={() => { if (navigator.share) navigator.share({ title: event?.title, url: publicUrl }); else copyLink(publicUrl, "Lien"); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 text-[#8B5CF6] text-xs font-medium hover:bg-[#8B5CF6]/20 transition-all"
          data-testid="share-native">
          <ExternalLink className="h-3.5 w-3.5" /> Partage natif / Email
        </button>
      </div>
    </div>
  );
}

function ProductionPanel({ eventId }) {
  const navigate = useNavigate();
  const lk = useLiveKit();

  const openProgramOutput = () => {
    window.open(
      `${window.location.origin}/program-output/${eventId}`,
      `glotech-program-${eventId}`,
      "width=1280,height=720,menubar=no,toolbar=no,location=no,status=no"
    );
    toast.success("Sortie PROGRAM ouverte dans une nouvelle fenetre");
  };

  return (
    <div className="p-3 space-y-3" data-testid="panel-production-content">
      <div className="bg-[#151B2B] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-1">
          <div className={`w-2 h-2 rounded-full ${lk.connectionState === "connected" ? "bg-[#10B981] animate-pulse" : "bg-[#64748b]"}`} />
          <span className="text-xs text-white font-medium">Etat de la conference</span>
        </div>
        <p className="text-[10px] text-[#64748b]">
          {lk.connectionState === "connected" ? `Active — ${lk.formatTime(lk.elapsed)} — ${lk.lkParticipants.length} participant(s)` : "Aucune conference active"}
        </p>
      </div>
      <div className="space-y-1.5">
        <button onClick={() => navigate(`/app/evenements/${eventId}/live-control`)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs font-medium hover:bg-[#EF4444]/20 transition-all"
          data-testid="prod-open-regie">
          <Radio className="h-3.5 w-3.5" /> Ouvrir la Regie
        </button>
        <button onClick={openProgramOutput}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#EC4899]/10 border border-[#EC4899]/30 text-[#EC4899] text-xs font-medium hover:bg-[#EC4899]/20 transition-all"
          data-testid="prod-open-program">
          <Maximize2 className="h-3.5 w-3.5" /> Sortie PROGRAM (Zoom)
        </button>
        <button onClick={() => navigate(`/app/live-meeting/${eventId}`)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] text-xs font-medium hover:bg-[#10B981]/20 transition-all"
          data-testid="prod-open-meeting">
          <Video className="h-3.5 w-3.5" /> Live Meeting
        </button>
      </div>
    </div>
  );
}

const PANEL_COMPONENTS = {
  conference: ConferencePanel,
  stats: StatsPanel,
  participants: ParticipantsPanel,
  regie: RegiePanel,
  chat: ChatPanel,
  scanner: ScannerPanel,
  sharing: SharingPanel,
  production: ProductionPanel,
};

// ===== MAIN COMPONENT =====
export default function ControlCenterPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [event, setEvent] = useState(null);
  const [layoutId, setLayoutId] = useState(4);
  const [panels, setPanels] = useState(["conference", "stats", "participants", "regie"]);
  const [showPanelPicker, setShowPanelPicker] = useState(null); // index to replace

  useEffect(() => {
    axios.get(`${API}/api/event/events/${eventId}`, { headers })
      .then(r => setEvent(r.data)).catch(() => {});
  }, [eventId]);

  // Adjust panels when layout changes
  useEffect(() => {
    const layout = LAYOUTS.find(l => l.id === layoutId);
    if (!layout) return;
    if (panels.length < layout.id) {
      const available = Object.keys(PANEL_REGISTRY).filter(k => !panels.includes(k));
      setPanels(p => [...p, ...available.slice(0, layout.id - p.length)]);
    } else if (panels.length > layout.id) {
      setPanels(p => p.slice(0, layout.id));
    }
  }, [layoutId]);

  const closePanel = (idx) => {
    setPanels(p => p.filter((_, i) => i !== idx));
    if (panels.length - 1 < layoutId) {
      const newLayout = LAYOUTS.filter(l => l.id <= panels.length - 1).pop();
      if (newLayout) setLayoutId(newLayout.id);
    }
  };

  const detachPanel = (idx, panelType) => {
    const url = panelType === "regie"
      ? `/app/evenements/${eventId}/live-control`
      : panelType === "conference"
      ? `/app/live-meeting/${eventId}`
      : `/app/control-center/${eventId}?detached=${panelType}`;

    window.open(
      `${window.location.origin}${url}`,
      `glotech-${panelType}-${eventId}`,
      "width=800,height=600,menubar=no,toolbar=no,location=no,status=no"
    );
    toast.success(`${PANEL_REGISTRY[panelType]?.label} detache dans une nouvelle fenetre`);
  };

  const replacePanel = (idx, newType) => {
    setPanels(p => p.map((t, i) => i === idx ? newType : t));
    setShowPanelPicker(null);
  };

  const layout = LAYOUTS.find(l => l.id === layoutId) || LAYOUTS[3];

  return (
    <div className="h-screen bg-[#080C14] flex flex-col overflow-hidden" data-testid="control-center-page">
      {/* Header */}
      <header className="h-11 bg-[#0B0F19] border-b border-[#1E293B] flex items-center justify-between px-3 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/app/evenements/${eventId}`)} className="text-[#64748b] hover:text-white p-1" data-testid="cc-back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="text-white text-sm font-semibold">Centre de controle</span>
          {event && <span className="text-[#64748b] text-xs hidden sm:inline">— {event.title}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          {LAYOUTS.map(l => {
            const LIcon = l.icon;
            return (
              <button key={l.id} onClick={() => setLayoutId(l.id)}
                className={`p-1.5 rounded-lg text-xs transition-all ${layoutId === l.id ? "bg-[#0099CC]/20 text-[#0099CC]" : "text-[#64748b] hover:text-white hover:bg-[#1E293B]"}`}
                title={l.label} data-testid={`layout-${l.id}`}>
                <LIcon className="h-4 w-4" />
              </button>
            );
          })}
        </div>
      </header>

      {/* Panels Grid */}
      <div className={`flex-1 grid ${layout.grid} gap-1.5 p-1.5 min-h-0`}>
        {panels.slice(0, layoutId).map((panelType, idx) => {
          const PanelComponent = PANEL_COMPONENTS[panelType];
          return (
            <PanelFrame key={`${idx}-${panelType}`} panelId={idx} panelType={panelType}
              onClose={closePanel} onDetach={detachPanel}>
              {PanelComponent && (
                <PanelComponent eventId={eventId} headers={headers} event={event} token={token} />
              )}
            </PanelFrame>
          );
        })}

        {/* Add panel button if space available */}
        {panels.length < layoutId && (
          <div className="bg-[#0B0F19] border border-dashed border-[#2A3441] rounded-xl flex items-center justify-center cursor-pointer hover:border-[#0099CC]/50 transition-all"
            onClick={() => setShowPanelPicker(panels.length)} data-testid="add-panel-btn">
            <div className="text-center">
              <div className="text-2xl text-[#2A3441] mb-1">+</div>
              <div className="text-xs text-[#4A5568]">Ajouter un panneau</div>
            </div>
          </div>
        )}
      </div>

      {/* Panel picker modal */}
      {showPanelPicker !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowPanelPicker(null)}>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-5 max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="panel-picker">
            <h3 className="text-white font-semibold mb-4">Choisir un panneau</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(PANEL_REGISTRY).map(([key, reg]) => {
                const Icon = reg.icon;
                const isActive = panels.includes(key);
                return (
                  <button key={key}
                    onClick={() => {
                      if (showPanelPicker < panels.length) {
                        replacePanel(showPanelPicker, key);
                      } else {
                        setPanels(p => [...p, key]);
                        setShowPanelPicker(null);
                      }
                    }}
                    disabled={isActive}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${isActive ? "border-[#2A3441] opacity-40" : "border-[#2A3441] hover:border-[#0099CC]/50"}`}
                    data-testid={`pick-${key}`}>
                    <Icon className="h-5 w-5 mb-2" style={{ color: reg.color }} />
                    <div className="text-white text-sm font-medium">{reg.label}</div>
                    {isActive && <div className="text-[10px] text-[#4A5568] mt-1">Deja actif</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
